import { SectionCard } from '@atlas/ui-components'
import { Button, Spinner } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
	useRefreshWorkfrontReference,
	useRunWorkfrontDiscovery,
	useSyncWorkfrontBundle,
	useWorkfrontHealth,
	useWorkfrontOverview,
} from '@/hooks/useWorkfront'
import { useMappingStatus } from '@/hooks/useWorkfrontMappings'
import { apiErrorMessage } from '@/lib/api-error'
import { formatUtc } from '@/lib/format-utc'
import { notification } from '@/notifications/notification-store'
import { OverviewTiles } from './components/OverviewTiles'
import { SyncBundleDialog } from './components/WorkfrontDialogs'
import { draftCountsLine } from './mappings/components/DraftBar'
import { useActorName } from './mappings/tab-support'
import { WORKFRONT_PATHS, WorkfrontHeader } from './shared'

/** The overview's three actions; each queues work and says so in the status bar. */
function OverviewActions() {
	const sync = useSyncWorkfrontBundle()
	const discovery = useRunWorkfrontDiscovery()
	const reference = useRefreshWorkfrontReference()
	const [syncing, setSyncing] = useState(false)

	const failed = (fallback: string) => (cause: unknown) => notification.error(apiErrorMessage(cause, fallback))
	return (
		<>
			<Button onPress={() => setSyncing(true)}>Sync one bundle</Button>
			<Button
				variant="outline"
				isDisabled={discovery.isPending}
				onPress={() =>
					discovery.mutate(undefined, {
						onSuccess: () => notification.success('Discovery run started.'),
						onError: failed('Could not start a discovery run.'),
					})
				}
			>
				Run discovery now
			</Button>
			<Button
				variant="outline"
				isDisabled={reference.isPending}
				onPress={() =>
					reference.mutate(undefined, {
						onSuccess: () => notification.success('Reference data refresh started.'),
						onError: failed('Could not refresh the reference data.'),
					})
				}
			>
				Refresh reference data
			</Button>
			<SyncBundleDialog
				open={syncing}
				submitting={sync.isPending}
				onCancel={() => setSyncing(false)}
				onSync={(referenceNumber) =>
					sync.mutate(referenceNumber, {
						onSuccess: () => {
							setSyncing(false)
							notification.success(`Bundle #${referenceNumber} queued for sync.`)
						},
						onError: failed(`Could not sync bundle #${referenceNumber}.`),
					})
				}
			/>
		</>
	)
}

/** The published mapping version, who published it and when, and the open draft if there is one. */
function MappingStatusCard() {
	const status = useMappingStatus()
	const nameOf = useActorName()
	const { latest, draft } = status.data ?? { latest: null, draft: null }
	return (
		<SectionCard
			title="Mappings"
			action={
				<Link className="text-ds-sm underline" to={WORKFRONT_PATHS.mappings}>
					Open Mappings
				</Link>
			}
		>
			{status.isLoading ? <Spinner aria-label="Loading the mapping status" /> : null}
			{status.isError ? (
				<p className="text-ds-sm text-ds-danger">
					{apiErrorMessage(status.error, 'Could not load the mapping status.')}
				</p>
			) : null}
			{status.data ? (
				<div className="space-y-1 text-ds-sm">
					<p>
						{latest
							? `Version ${latest.version}, published by ${nameOf(latest.publishedBy)}, ${formatUtc(latest.publishedAt)}: ${latest.summary}`
							: 'Nothing published yet.'}
					</p>
					<p className="text-ds-text-muted">
						{draft ? `Draft open by ${nameOf(draft.openedBy)}: ${draftCountsLine(draft.counts)}` : 'No draft open.'}
					</p>
				</div>
			) : null}
		</SectionCard>
	)
}

/** Admin › Workfront: the connection, the bundles, the mappings, and the open problems at a glance. The discovery streams have their own section. */
export function WorkfrontOverviewPage() {
	const overview = useWorkfrontOverview()
	const health = useWorkfrontHealth()
	return (
		<div className="space-y-6">
			<WorkfrontHeader title="Workfront" actions={<OverviewActions />} />
			{overview.isLoading ? <Spinner aria-label="Loading the overview" /> : null}
			{overview.isError ? (
				<p className="text-ds-sm text-ds-danger">{apiErrorMessage(overview.error, 'Could not load the overview.')}</p>
			) : null}
			{overview.data ? (
				<>
					<OverviewTiles overview={overview.data} health={health.data ?? null} />
					<MappingStatusCard />
				</>
			) : null}
		</div>
	)
}
